import React, { useEffect, useMemo, useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import {
	SypacIcon,
	SypacTabs,
	SypacText,
} from '@sypac/component-library-react';
import SearchBar from '../../../components/SearchBar/SearchBar';
import useDebounce from '../../../hooks/useDebounce';
import { TabsButton } from '../../TabsButton/TabsButton';
import { AdminContracts } from '../../../assets/Contracts/AdminContracts';
import Tons from '../../../assets/Tons';
import { NumericFormat } from 'react-number-format';
import PriceTag from '../../../assets/PriceTag';
import { Contract } from '../../../services/contracts.service';
import { BlueBox } from '../../../assets/BlueBox';
import { TruckApprove } from '../../../assets/TruckApprove';
import { TruckNotApprove } from '../../../assets/TruckNotApprove';
import { OrangeCircle } from '../../../assets/DeliveryShapes/OrangeCircle';
import { GreenSquare } from '../../../assets/DeliveryShapes/GreenSquare';
import DashedDivider from '../../DashedDivider/DashedDivider';
import { Stack } from '../../../assets/Stack';
import { formatDate, formatTimeSince } from '../../../utils/time.util';
import StatusBadge from '../../StatusBadge/StatusBadge';
import { OrderTarget } from '../../OrdersTable/interfaces/OrderStatus.interface';
import {
	CUSTOMER_STEPS_MULTIPLE,
	CUSTOMER_STEPS_MULTIPLE_EXCLUDED,
	CUSTOMER_STEPS_SIMPLE,
	CUSTOMER_STEPS_SIMPLE_EXCLUDED,
	CUSTOMER_SUB_STATUS_MULTIPLE,
	CUSTOMER_SUB_STATUS_MULTIPLE_EXCLUDED,
	CUSTOMER_SUB_STATUS_SIMPLE,
	CUSTOMER_SUB_STATUS_SIMPLE_EXCLUDED,
	PRODUCER_STEPS,
	PRODUCER_STEPS_EXCLUDED,
	PRODUCER_STEPS_EXCLUDED_WITH_NOTES,
	PRODUCER_STEPS_WITH_NOTES,
	PRODUCER_SUB_STATUS,
	PRODUCER_SUB_STATUS_EXCLUDED,
	PRODUCER_SUB_STATUS_EXCLUDED_WITH_NOTES,
	PRODUCER_SUB_STATUS_WITH_NOTES,
} from '../../OrderDetailsStepper/stepData';
import { OrderInterface } from '../../OrdersTable/interfaces/Order.interface';
import {
	OrderStatesEnum,
	OrderStatusesEnum,
} from '../../../services/orders.services';
import { resolveExecutionStatus } from '../../../utils/order-execution.util';
import {
	BillingService,
	PaymentNotesStatusesEnum,
} from '../../../services/billing.service';
import { cn } from '../../../lib/utils';
import LayeredProgressBar from '../../LayeredProgressBar/LayeredProgressBar';
import { getAdminProgress, getAdminStepSets } from './steps/adminProgress';
import { Tooltip } from 'react-tooltip';
import { normalizeForSearch } from '../../../utils/string.util';
import { MutatingDots } from 'react-loader-spinner';
import {
	DateRangePicker,
	DateRangeValue,
} from '../../DatePicker/DateRangePicker';
import dayjs from 'dayjs';

interface OrdersTabProps {
	contract: Contract;
	role: OrderTarget;
	onOrderClick?: (orderId: number) => void;
}

const OrdersTab: React.FC<OrdersTabProps> = ({
	contract,
	role,
	onOrderClick,
}) => {
	const { t } = useTranslate();
	const language = localStorage.getItem('lang') || 'en';
	const [invoiceStatusByOrder, setInvoiceStatusByOrder] = useState<
		Record<string, PaymentNotesStatusesEnum | null>
	>({});
	const [invoiceFetchedByOrder, setInvoiceFetchedByOrder] = useState<
		Record<string, boolean>
	>({});
	const [searchLocal, setSearchLocal] = useState<string>('');
	const searchQuery = useDebounce(searchLocal, 500);
	const [selectedTab, setSelectedTab] = useState<string>('all');
	const [statuses, setStatuses] = useState<string[]>([]);
	const [deliveryDateRange, setDeliveryDateRange] = useState<DateRangeValue>({
		from: null,
		to: null,
	});

	const setCurrentTab = (next: string[]) => {
		setStatuses(next);
	};

	const filteredOrders = useMemo(() => {
		const orders = (contract?.orders ?? []) as OrderInterface[];

		const q = normalizeForSearch(String(searchQuery || '').trim());

		const filtered = orders.filter((o) => {
			const status = String(o?.status ?? '').toLowerCase();
			const state = String(o?.state ?? '').toLowerCase();

			const matchesTab =
				!statuses.length ||
				statuses.includes(status) ||
				statuses.includes(state);

			if (!matchesTab) return false;
			if (!q) return true;

			const idStr = normalizeForSearch(String(o?.orderId ?? o?.id ?? ''));
			const deliveryAddress = normalizeForSearch(
				String(o?.deliveryAddress ?? ''),
			);

			return idStr.includes(q) || deliveryAddress.includes(q);
		});

		const normalizeDayTs = (raw: any) => {
			if (!raw) return null;
			const d = raw instanceof Date ? new Date(raw) : new Date(raw);
			const ts = d.getTime();
			if (!Number.isFinite(ts)) return null;
			d.setHours(0, 0, 0, 0);
			return d.getTime();
		};

		const fromTs = deliveryDateRange?.from
			? normalizeDayTs(deliveryDateRange.from)
			: null;

		const toTs = deliveryDateRange?.to
			? normalizeDayTs(deliveryDateRange.to)
			: null;

		if (fromTs == null) return filtered;

		// single-day filter
		if (toTs == null) {
			return filtered.filter((o) => {
				const dt = normalizeDayTs(o?.deliveryTo);
				return dt != null && dt === fromTs;
			});
		}

		// interval filter (inclusive)
		const minTs = Math.min(fromTs, toTs);
		const maxTs = Math.max(fromTs, toTs);

		return filtered.filter((o) => {
			const dt = normalizeDayTs(o?.deliveryTo);
			return dt != null && dt >= minTs && dt <= maxTs;
		});
	}, [contract?.orders, searchQuery, statuses, deliveryDateRange]);

	const pickOrderKey = (o: OrderInterface) => String(o?.id ?? o?.orderId ?? '');

	const shouldFetchProducerInvoice = (o: OrderInterface) => {
		if (!o) return false;
		if (o.deliveryIncluded) {
			return [
				OrderStatesEnum.LOADED,
				OrderStatesEnum.TO_UNLOADING,
				OrderStatesEnum.ARRIVED_TO_UNLOADING,
				OrderStatesEnum.UNLOADING,
				OrderStatesEnum.UNLOADED,
				OrderStatesEnum.COMPLETED,
			].includes(o.state as OrderStatesEnum);
		}
		return [OrderStatusesEnum.PENDING_PAYMENT, OrderStatusesEnum.PAID].includes(
			o.status as OrderStatusesEnum,
		);
	};

	useEffect(() => {
		setInvoiceStatusByOrder({});
		setInvoiceFetchedByOrder({});
	}, [contract?.id, role]);

	useEffect(() => {
		if (role !== OrderTarget.producer) return;

		const orders = (contract?.orders ?? []) as OrderInterface[];

		const toFetch = orders.filter((o) => {
			const key = pickOrderKey(o);
			return (
				key &&
				shouldFetchProducerInvoice(o) &&
				invoiceStatusByOrder[key] === undefined
			);
		});

		if (!toFetch.length) return;

		let cancelled = false;

		(async () => {
			const entries = await Promise.all(
				toFetch.map(async (o) => {
					const key = pickOrderKey(o);
					try {
						const { data } = await BillingService.getInvoiceById(
							o.target,
							o.id,
						);
						return [
							key,
							(data?.status as PaymentNotesStatusesEnum) ?? null,
						] as const;
					} catch {
						// important: still mark as fetched to avoid waiting forever
						return [key, null] as const;
					}
				}),
			);

			if (cancelled) return;

			setInvoiceStatusByOrder((prev) => {
				const next = { ...prev };
				for (const [k, v] of entries) next[k] = v;
				return next;
			});

			setInvoiceFetchedByOrder((prev) => {
				const next = { ...prev };
				for (const [k] of entries) next[k] = true;
				return next;
			});
		})();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [role, contract?.orders, invoiceFetchedByOrder]);

	const invoiceRequiredKeys = useMemo(() => {
		if (role !== OrderTarget.producer) return [];
		const orders = (contract?.orders ?? []) as OrderInterface[];
		return orders
			.filter(shouldFetchProducerInvoice)
			.map(pickOrderKey)
			.filter(Boolean);
	}, [role, contract?.orders]);

	const invoicesReady =
		role !== OrderTarget.producer ||
		invoiceRequiredKeys.every((k) => invoiceFetchedByOrder[k] === true);

	const getCustomerStepSets = (order: OrderInterface) => {
		const simple = (order?.trucks?.length ?? 0) <= 1;
		const deliveryIncluded = !!order?.deliveryIncluded;

		if (simple) {
			return deliveryIncluded
				? { steps: CUSTOMER_STEPS_SIMPLE, subs: CUSTOMER_SUB_STATUS_SIMPLE }
				: {
						steps: CUSTOMER_STEPS_SIMPLE_EXCLUDED,
						subs: CUSTOMER_SUB_STATUS_SIMPLE_EXCLUDED,
				  };
		} else {
			return deliveryIncluded
				? { steps: CUSTOMER_STEPS_MULTIPLE, subs: CUSTOMER_SUB_STATUS_MULTIPLE }
				: {
						steps: CUSTOMER_STEPS_MULTIPLE_EXCLUDED,
						subs: CUSTOMER_SUB_STATUS_MULTIPLE_EXCLUDED,
				  };
		}
	};

	const getCustomerProgress = (order: OrderInterface) => {
		const simple = (order?.trucks?.length ?? 0) <= 1;
		const included = !!order?.deliveryIncluded;
		const status = String(order?.status || '').toUpperCase();
		const state = String(resolveExecutionStatus(order) || '').toUpperCase();
		const trucks = Array.isArray(order?.trucks) ? order.trucks : [];

		if (simple && included) {
			const statusMap: Record<string, { current: number; final?: boolean }> = {
				PAID: { current: 6, final: true },
				LOADED: { current: 3 },
				TO_UNLOADING: { current: 3 },
				ARRIVED_TO_UNLOADING: { current: 4 },
				UNLOADING: { current: 4 },
				UNLOADED: { current: 4 },
				DELIVERED: { current: 5 },
				COMPLETED: { current: 5 },
			};

			const stateMap: Record<string, number> = {
				TO_DO: 1,
				AWAITING_CONFIRMATION: 1,
				TRANSPORTER_CONFIRMED: 2,
				TRUCK_CONFIRMED: 2,
				REQUESTED: 2,
				ACCEPTED: 2,
				TO_LOADING: 2,
				ARRIVED_TO_LOADING: 2,
				LOADING: 2,
				LOADED: 3,
				TO_UNLOADING: 3,
				ARRIVED_TO_UNLOADING: 4,
				UNLOADING: 4,
				UNLOADED: 4,
				COMPLETED: 5,
			};

			if (status === 'PAID') return { current: 6, isFinalStep: true };

			const fromState =
				statusMap[state] ??
				(stateMap[state] ? { current: stateMap[state] } : undefined);

			const fromStatus =
				statusMap[status] ??
				(stateMap[status] ? { current: stateMap[status] } : undefined);

			const picked = fromState ?? fromStatus ?? { current: 1 };

			return { current: picked.current, isFinalStep: !!picked.final };
		}

		if (simple && !included) {
			const map: Record<string, { current: number; final?: boolean }> = {
				PAID: { current: 5, final: true },
				DELIVERED: { current: 4 },
				IN_PROGRESS: { current: 3 },
				PREPARING: { current: 2 },
				CREATED: { current: 1 },
			};
			const found = map[status] || map.CREATED;
			return { current: found.current, isFinalStep: !!found.final };
		}

		if (!simple && included) {
			const allFleetDone =
				trucks.length > 0 &&
				trucks.every(
					(t) =>
						String(t?.orderStatus || '').toLowerCase() === 'completed' ||
						!!t?.unloadDetails,
				);

			if (status === 'PAID') return { current: 5, isFinalStep: true };

			if (allFleetDone) return { current: 4, isFinalStep: false };

			if (state === 'COMPLETED' && trucks.length === 0)
				return { current: 4, isFinalStep: false };

			const statusMap: Record<string, number> = {
				PREPARING: 3,
				TO_UNLOADING: 3,
				ARRIVED_TO_UNLOADING: 3,
				DELIVERED: 3,
			};
			if (statusMap[status])
				return { current: statusMap[status], isFinalStep: false };

			const stateMap: Record<string, number> = {
				AWAITING_CONFIRMATION: 2,
				TRANSPORTER_CONFIRMED: 2,
				TRUCK_CONFIRMED: 2,
				TO_DO: 1,
			};

			return { current: stateMap[state] ?? 1, isFinalStep: false };
		}

		const hasAnyProducerLoad = trucks.some(
			(t) => t?.producerLoadDetails != null,
		);
		const allProducerLoad =
			trucks.length > 0 && trucks.every((t) => t?.producerLoadDetails != null);

		const map: Record<string, { current: number; final?: boolean }> = {
			PAID: { current: 5, final: true },
			CREATED: { current: 1 },
			PREPARING: { current: hasAnyProducerLoad ? 3 : 2 },
			IN_PROGRESS: { current: hasAnyProducerLoad ? 3 : 2 },
			DELIVERED: { current: allProducerLoad ? 4 : 3 },
		};

		const found = map[status] || map.CREATED;
		return { current: found.current, isFinalStep: !!found.final };
	};

	const getProducerStepSets = (
		order: OrderInterface,
		invoiceStatus?: PaymentNotesStatusesEnum | null,
	) => {
		const included = !!order?.deliveryIncluded;
		const withNotes =
			invoiceStatus === PaymentNotesStatusesEnum.PENDING_INVOICE ||
			invoiceStatus === PaymentNotesStatusesEnum.UPLOADED_INVOICE ||
			invoiceStatus === PaymentNotesStatusesEnum.INVOICE_CONFIRMED;

		if (included) {
			return withNotes
				? {
						steps: PRODUCER_STEPS_WITH_NOTES,
						subs: PRODUCER_SUB_STATUS_WITH_NOTES,
				  }
				: { steps: PRODUCER_STEPS, subs: PRODUCER_SUB_STATUS };
		}

		return withNotes
			? {
					steps: PRODUCER_STEPS_EXCLUDED_WITH_NOTES,
					subs: PRODUCER_SUB_STATUS_EXCLUDED_WITH_NOTES,
			  }
			: { steps: PRODUCER_STEPS_EXCLUDED, subs: PRODUCER_SUB_STATUS_EXCLUDED };
	};

	const getProducerProgress = (
		order: OrderInterface,
		invoiceStatus?: PaymentNotesStatusesEnum | null,
	) => {
		const included = !!order?.deliveryIncluded;
		const status = String(order?.status || '').toLowerCase();
		const state = String(resolveExecutionStatus(order) || '').toLowerCase();

		const withNotes =
			invoiceStatus === PaymentNotesStatusesEnum.PENDING_INVOICE ||
			invoiceStatus === PaymentNotesStatusesEnum.UPLOADED_INVOICE ||
			invoiceStatus === PaymentNotesStatusesEnum.INVOICE_CONFIRMED;

		if (included && withNotes) {
			const statusMap: Record<string, { current: number; final?: boolean }> = {
				[PaymentNotesStatusesEnum.PENDING_INVOICE]: { current: 4 },
				[PaymentNotesStatusesEnum.UPLOADED_INVOICE]: { current: 5 },
				[PaymentNotesStatusesEnum.INVOICE_CONFIRMED]: { current: 6 },
				paid: { current: PRODUCER_STEPS_WITH_NOTES.length, final: true },
			};
			const stateMap: Record<string, number> = {
				arrived_to_loading: 3,
				loading: 3,
				to_loading: 2,
				requested: 2,
				accepted: 2,
				truck_confirmed: 2,
				transporter_confirmed: 2,
				to_do: 1,
			};
			const key = (invoiceStatus ?? status) as string;
			const found =
				statusMap[key] ??
				(stateMap[state] ? { current: stateMap[state] } : { current: 1 });
			return { current: found.current, isFinalStep: !!found.final };
		}

		if (included && !withNotes) {
			const statusMap: Record<string, { current: number; final?: boolean }> = {
				paid: { current: PRODUCER_STEPS.length, final: true },
				delivered: { current: 4 },
			};
			const stateMap: Record<string, number> = {
				loaded: 4,
				to_unloading: 4,
				arrived_to_unloading: 4,
				unloading: 4,
				unloaded: 4,
				completed: 4,
				arrived_to_loading: 3,
				loading: 3,
				to_loading: 2,
				requested: 2,
				accepted: 2,
				truck_confirmed: 2,
				transporter_confirmed: 2,
				to_do: 1,
			};
			const found =
				statusMap[status] ??
				(stateMap[state] ? { current: stateMap[state] } : { current: 1 });
			return { current: found.current, isFinalStep: !!found.final };
		}

		if (!included && withNotes) {
			const statusMap: Record<string, { current: number; final?: boolean }> = {
				[PaymentNotesStatusesEnum.PENDING_INVOICE]: { current: 5 },
				[PaymentNotesStatusesEnum.UPLOADED_INVOICE]: { current: 6 },
				[PaymentNotesStatusesEnum.INVOICE_CONFIRMED]: { current: 7 },
				paid: {
					current: PRODUCER_STEPS_EXCLUDED_WITH_NOTES.length,
					final: true,
				},
			};
			const stateMap: Record<string, number> = {
				loaded: 4,
				arrived_to_loading: 3,
				loading: 3,
				to_loading: 2,
				requested: 2,
				accepted: 2,
				truck_confirmed: 2,
				transporter_confirmed: 2,
				to_do: 1,
			};
			const key = (invoiceStatus ?? status) as string;
			const found =
				statusMap[key] ??
				(stateMap[state] ? { current: stateMap[state] } : { current: 1 });
			return { current: found.current, isFinalStep: !!found.final };
		}

		const statusMap: Record<string, { current: number; final?: boolean }> = {
			paid: { current: PRODUCER_STEPS_EXCLUDED.length, final: true },
			delivered: { current: 5 },
			in_progress: { current: 2 },
		};
		const stateMap: Record<string, number> = {
			loaded: 4,
			arrived_to_loading: 3,
			loading: 3,
			to_loading: 2,
			requested: 2,
			accepted: 2,
			truck_confirmed: 2,
			transporter_confirmed: 2,
			to_do: 1,
		};
		const found =
			statusMap[status] ??
			(stateMap[state] ? { current: stateMap[state] } : { current: 1 });
		return { current: found.current, isFinalStep: !!found.final };
	};

	const resolveStepsByRole = (order: OrderInterface) => {
		const trucksCount = order?.trucks?.length ?? 0;

		if (role === OrderTarget.customer) {
			const { steps, subs } = getCustomerStepSets(order);
			const { current, isFinalStep } = getCustomerProgress(order);
			const subIndex = Math.max(0, current - 1);
			return { steps, subs, current, isFinalStep, subIndex };
		}

		if (role === OrderTarget.producer) {
			const key = String(order?.id ?? order?.orderId ?? '');
			const invoiceStatus = invoiceStatusByOrder[key];

			const { steps, subs } = getProducerStepSets(order, invoiceStatus);
			const { current, isFinalStep } = getProducerProgress(
				order,
				invoiceStatus,
			);

			const subIndex = Math.max(0, current - 1);
			return { steps, subs, current, isFinalStep, subIndex };
		}

		const { steps, subs } = getAdminStepSets(order);
		const { current, isFinalStep } = getAdminProgress(order);
		const subIndex =
			!order?.deliveryIncluded && trucksCount > 1 && current === 2
				? current - 2
				: current - 1;

		return { steps, subs, current, isFinalStep, subIndex };
	};

	const tabDefs = useMemo(() => {
		if (role === OrderTarget.customer) {
			return [
				{ label: t('orders.all', 'All'), buttonId: 'all', statuses: [] },
				{
					label: t('orders.reviewing', 'Reviewing'),
					buttonId: 'created',
					statuses: ['created'],
				},
				{
					label: t('orders.preparingOrder', 'Preparing order'),
					buttonId: 'preparing',
					statuses: [
						'preparing',
						'to_unloading',
						'in_progress',
						'arrived_to_unloading',
					],
				},
				{
					label: t('orders.delivered', 'Delivered'),
					buttonId: 'delivered',
					statuses: ['delivered'],
				},
				{
					label: t('orders.completed', 'Completed'),
					buttonId: 'completed',
					statuses: ['paid'],
				},
			];
		}

		if (role === OrderTarget.producer) {
			return [
				{ label: t('orders.all', 'All'), buttonId: 'all', statuses: [] },
				{
					label: t('ordersList.toDo', 'To do'),
					buttonId: 'to_do',
					statuses: ['to_do'],
				},
				{
					label: t('ordersList.progress', 'In progress'),
					buttonId: 'progress',
					statuses: ['in_progress', 'loading', 'loaded', 'confirm_pick_up'],
				},
				{
					label: t('ordersList.delivered', 'Delivered'),
					buttonId: 'delivered',
					statuses: ['delivered', 'pending_payment'],
				},
				{
					label: t('ordersList.completed', 'Completed'),
					buttonId: 'completed',
					statuses: ['paid', 'completed'],
				},
			];
		}

		return [
			{ label: t('orders.all', 'All'), buttonId: 'all', statuses: [] },
			{
				label: t('orders.toDo', 'To do'),
				buttonId: 'to_do',
				statuses: ['to_do'],
			},
			{
				label: t('orders.inProgress', 'Preparing order'),
				buttonId: 'progress',
				statuses: [
					'in_progress',
					'transporter_confirmed',
					'truck_confirmed',
					'to_loading',
					'to_unloading',
					'awaiting_confirmation',
				],
			},
			{
				label: t('orders.delivered', 'Delivered'),
				buttonId: 'delivered',
				statuses: ['delivered'],
			},
			{
				label: t('orders.completed', 'Completed'),
				buttonId: 'completed',
				statuses: ['paid'],
			},
		];
	}, [role, t]);

	const line0 = contract?.lines?.[0];

	const { orderedQtyTons, deliveredQtyTons } = useMemo(() => {
		const orders = (contract?.orders ?? []) as OrderInterface[];

		if (!orders.length) {
			return {
				orderedQtyTons: Number(line0?.orderedQtyTons ?? 0),
				deliveredQtyTons: Number(line0?.deliveredQtyTons ?? 0),
			};
		}

		let ordered = 0;
		let delivered = 0;

		for (const order of orders) {
			const trucks = order?.trucks ?? [];

			for (const truck of trucks) {
				const hasLoadedQuantity =
					truck?.loadedQuantity !== null &&
					truck?.loadedQuantity !== undefined &&
					!Number.isNaN(Number(truck.loadedQuantity));

				if (hasLoadedQuantity) {
					delivered += Number(truck.loadedQuantity) || 0;
				} else {
					ordered += Number(truck?.payload ?? 0) || 0;
				}
			}
		}

		return { orderedQtyTons: ordered, deliveredQtyTons: delivered };
	}, [contract?.orders, line0?.orderedQtyTons, line0?.deliveredQtyTons]);

	const totalOrderedQtyTons = orderedQtyTons + deliveredQtyTons;

	const hasHugeQtyNumbers = [
		line0?.contractQtyTons,
		totalOrderedQtyTons,
		deliveredQtyTons,
	]
		.filter((v) => v != null)
		.some((v) => Number(v) > 10_000);

	const customerPlTabsRow1 = useMemo(() => tabDefs.slice(0, 3), [tabDefs]);
	const customerPlTabsRow2 = useMemo(() => tabDefs.slice(3), [tabDefs]);

	const escapeHtml = (s: string) =>
		s
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');

	const foldWithIndexMap = (input: string) => {
		const indexMap: number[] = [];
		let folded = '';

		for (let i = 0; i < input.length; i++) {
			const ch = input[i];

			let out = ch
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.replace(/[łŁ]/g, 'l')
				.toLowerCase();

			for (let j = 0; j < out.length; j++) {
				folded += out[j];
				indexMap.push(i);
			}
		}

		return { folded, indexMap };
	};

	const highlightSearchInsensitive = (text: string, rawQuery: string) => {
		const q = (rawQuery || '').trim();
		if (!q) return escapeHtml(text);

		const { folded: foldedText, indexMap } = foldWithIndexMap(text);
		const { folded: foldedQuery } = foldWithIndexMap(q);

		if (!foldedQuery) return escapeHtml(text);

		const ranges: Array<[number, number]> = [];
		let from = 0;

		while (true) {
			const start = foldedText.indexOf(foldedQuery, from);
			if (start === -1) break;

			const endFolded = start + foldedQuery.length - 1;
			const startOrig = indexMap[start];
			const endOrig = indexMap[endFolded] + 1;

			ranges.push([startOrig, endOrig]);
			from = start + foldedQuery.length;
		}

		if (!ranges.length) return escapeHtml(text);

		ranges.sort((a, b) => a[0] - b[0]);
		const merged: Array<[number, number]> = [];
		for (const r of ranges) {
			const last = merged[merged.length - 1];
			if (!last || r[0] > last[1]) merged.push(r);
			else last[1] = Math.max(last[1], r[1]);
		}

		let out = '';
		let cursor = 0;

		for (const [s, e] of merged) {
			out += escapeHtml(text.slice(cursor, s));
			out += `<span class="text-texas-rose">${escapeHtml(
				text.slice(s, e),
			)}</span>`;
			cursor = e;
		}

		out += escapeHtml(text.slice(cursor));
		return out;
	};

	return (
		<>
			<div className="flex flex-1 min-h-0 flex-col gap-5 mt-[6px] overflow-hidden">
				<div
					className={cn(
						'flex px-[40px]',
						hasHugeQtyNumbers
							? 'flex-col gap-5 items-stretch'
							: 'flex-row gap-[40px] items-center',
					)}
				>
					<div className="flex gap-[14px] items-center">
						<div className="w-[92px] h-[80px] flex flex-col gap-[6px] items-center justify-center border border-solid border-gray-10 rounded-10 box-border">
							<SypacText variant="body-regular-medium">
								<p className="text-[12px] leading-[14px] text-gray-60">
									<T keyName="clientSelectQuantity.placed">Placed</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-[20px] leading-[32px] text-gray-80">
									{contract.orders?.length ?? 0}
								</p>
							</SypacText>
						</div>
						<div className="w-[92px] h-[80px] flex flex-col gap-[6px] items-center justify-center border border-solid border-gray-10 rounded-10 box-border">
							<SypacText variant="body-regular-medium">
								<p className="text-[12px] leading-[14px] text-gray-60">
									<T keyName="clientSelectQuantity.inProgress">In progress</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-[20px] leading-[32px] text-gray-80">
									{contract.ordersInProgress ?? 0}
								</p>
							</SypacText>
						</div>
						<div className="w-[92px] h-[80px] flex flex-col gap-[6px] items-center justify-center border border-solid border-gray-10 rounded-10 box-border">
							<SypacText variant="body-regular-medium">
								<p className="text-[12px] leading-[14px] text-gray-60">
									<T keyName="clientSelectQuantity.delivered">Delivered</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-[20px] leading-[32px] text-gray-80">
									{contract.ordersDelivered ?? 0}
								</p>
							</SypacText>
						</div>
					</div>

					<LayeredProgressBar
						type={{
							key: 'contractQuantity',
							label: 'Contract quantity',
						}}
						totalTons={line0?.contractQtyTons ?? 0}
						segments={[
							{
								value: orderedQtyTons,
								colorClass: 'bg-mountain-meadow',
								tooltip: `${t(
									'layeredProgressBar.ordered',
									'Ordered:',
								)} ${orderedQtyTons} t`,
								enableBrightnessHover: true,
							},
							{
								value: deliveredQtyTons,
								colorClass: 'bg-texas-rose',
								tooltip: `${t(
									'layeredProgressBar.delivered',
									'Delivered:',
								)} ${deliveredQtyTons} t`,
								enableBrightnessHover: true,
							},
						]}
						valueSuffix=" t"
						showSummary
						contractStatus={contract?.status}
					/>
				</div>
				<div className="border-0 border-solid border-t-[1px] border-gray-10" />

				<div className="flex flex-col items-center gap-[14px] px-[40px] -mt-[6px]">
					{[OrderTarget.producer, OrderTarget.customer].includes(
						role as OrderTarget,
					) && language === 'pl' ? (
						<div className="w-full flex flex-col gap-[6px]">
							<SypacTabs className="flex w-full p-[3px] gap-[4px]">
								{customerPlTabsRow1.map(({ label, buttonId, statuses }) => (
									<TabsButton
										key={buttonId}
										label={label}
										buttonId={buttonId}
										onClick={() => setCurrentTab(statuses)}
										isSelected={selectedTab === buttonId}
										handleSelectedTab={() => setSelectedTab(buttonId)}
										notification={0}
										classNames="w-full"
									/>
								))}
							</SypacTabs>

							{customerPlTabsRow2.length > 0 ? (
								<SypacTabs className="flex w-full p-[3px] gap-[4px]">
									{customerPlTabsRow2.map(({ label, buttonId, statuses }) => (
										<TabsButton
											key={buttonId}
											label={label}
											buttonId={buttonId}
											onClick={() => setCurrentTab(statuses)}
											isSelected={selectedTab === buttonId}
											handleSelectedTab={() => setSelectedTab(buttonId)}
											notification={0}
											classNames="w-full"
										/>
									))}
								</SypacTabs>
							) : null}
						</div>
					) : (
						<SypacTabs className="flex w-full p-[3px] gap-[4px]">
							{tabDefs.map(({ label, buttonId, statuses }) => (
								<TabsButton
									key={buttonId}
									label={label}
									buttonId={buttonId}
									onClick={() => setCurrentTab(statuses)}
									isSelected={selectedTab === buttonId}
									handleSelectedTab={() => setSelectedTab(buttonId)}
									notification={0}
								/>
							))}
						</SypacTabs>
					)}

					<div className="w-full flex items-center gap-[14px] ml-auto">
						<SearchBar
							placeholder={t(
								'adminOrders.searchByOrderId',
								'Search by Order ID',
							)}
							onChange={setSearchLocal}
							showButton={false}
							classNames="w-full"
						/>
						<DateRangePicker
							min={dayjs().format('YYYY-MM-DD')}
							className="min-w-[280px]"
							label={t('orders.filterByDate', 'Filter by date')}
							locale={language}
							value={deliveryDateRange}
							onChange={setDeliveryDateRange}
							calendarPosition="right"
						/>
					</div>
				</div>

				<div className="flex flex-col gap-[18px] px-[40px] -mt-[14px] pt-[14px] overflow-y-auto scrollbar-custom scroll-smooth">
					{!invoicesReady ? (
						<div className="flex items-center justify-center mt-[60px]">
							<MutatingDots
								height="166"
								width="100"
								color="#7693F4"
								secondaryColor="#494C83"
								radius="12.5"
								ariaLabel="mutating-dots-loading"
								visible
							/>
						</div>
					) : filteredOrders.length ? (
						filteredOrders.map((order, idx) => {
							const trucks = order?.trucks ?? [];
							const trucksCount = trucks.length;

							// @ts-ignore
							const { deliveredTrucks, inProgressTrucks } = trucks.reduce(
								(acc: any, truck) => {
									if (order?.deliveryIncluded) {
										if (truck?.unloadDetails) {
											acc.deliveredTrucks += 1;
										} else if (truck?.loadDetails) {
											acc.inProgressTrucks += 1;
										}
									} else {
										const hasProducerLoadDetails =
											truck?.producerLoadDetails != null;

										if (hasProducerLoadDetails) {
											acc.deliveredTrucks += 1;
										}
									}

									return acc;
								},
								{
									deliveredTrucks: 0,
									inProgressTrucks: 0,
								},
							);

							const toBeDoneTrucks = Math.max(
								0,
								trucksCount - deliveredTrucks - inProgressTrucks,
							);

							const totalTrucksForFleet =
								deliveredTrucks + inProgressTrucks + toBeDoneTrucks;

							const idForUi = order?.orderId ?? order?.id;

							const qtyTons = order?.quantity ?? 0;

							const totalWithVat =
								Number(order?.productPriceForClientWithVat ?? 0) +
								Number(order?.transportPriceForClientWithVat ?? 0);

							const pickupAddr = order?.pickupAddress || '—';
							const deliveryAddr = order?.deliveryAddress || '—';

							const rawSearch = String(searchQuery || '').trim();
							const idHtml = highlightSearchInsensitive(
								String(idForUi ?? '—'),
								rawSearch,
							);
							const deliveryHtml = highlightSearchInsensitive(
								String(deliveryAddr ?? '—'),
								rawSearch,
							);

							const { steps, subs, current, isFinalStep, subIndex } =
								resolveStepsByRole(order);
							const stepIdx = Math.min(
								Math.max(0, current - 1),
								steps.length - 1,
							);
							const subIdx = Math.min(Math.max(0, subIndex), subs.length - 1);

							const displayedStepKey = steps[stepIdx];
							const displayedSubKey = subs[subIdx];

							return (
								<div
									key={String(order?.id ?? order?.orderId ?? idx)}
									className="flex flex-col gap-3 py-[10px] border border-solid bg-white border-gray-10 rounded-10 transition hover:border-primary-violet hover:shadow-dropdown cursor-pointer"
									onClick={() => {
										if (order?.id) onOrderClick?.(order.id);
									}}
								>
									<div className="flex items-center justify-between px-[10px]">
										<div className="h-[24px] flex items-center gap-[6px]">
											<AdminContracts width={20} height={20} />
											{trucksCount > 1 ? <Stack /> : <BlueBox />}
											{order?.deliveryIncluded ? (
												<TruckApprove />
											) : (
												<TruckNotApprove />
											)}
											<div className="w-[2px] h-[2px] bg-gray-40 rounded-full mt-[2px]" />
											<SypacText variant="body-regular-medium">
												<p className="text-[14px] leading-[20px] text-gray-40 mt-[2px]">
													ID:{' '}
													<span
														dangerouslySetInnerHTML={{
															__html: idHtml,
														}}
													/>
												</p>
											</SypacText>
											<div className="w-[2px] h-[2px] bg-gray-40 rounded-full mt-[2px]" />
											<SypacText variant="body-regular-medium">
												<p className="text-[14px] leading-[20px] text-gray-40 mt-[2px]">
													{formatTimeSince(order?.createdAt, false)}
												</p>
											</SypacText>
										</div>

										<StatusBadge
											status={
												order.status === 'in_progress'
													? order.status
													: role === OrderTarget.producer &&
													  order.deliveryIncluded
													? order.state
													: order.status
											}
											isState={
												role === OrderTarget.producer &&
												order.deliveryIncluded &&
												order.status !== 'in_progress'
											}
										/>
									</div>

									<div className="border-0 border-solid border-t-[1px] border-gray-10" />

									<div className="flex w-full gap-[4px] px-[10px] box-border">
										{steps.map((_, idx) => {
											const isCurrent = idx === current - 1;
											const isPassed = idx < current - 1;

											const colorClass = isFinalStep
												? 'bg-shadow-blue'
												: isCurrent
												? 'bg-texas-rose'
												: isPassed
												? 'bg-[#8688AD]'
												: 'bg-light-grey';

											return (
												<div
													key={idx}
													className={`w-full h-[10px] rounded ${colorClass}`}
												/>
											);
										})}
									</div>

									<div className="flex gap-5 px-[10px]">
										<div className="w-[54px] h-[54px] flex items-center justify-center bg-cornflower-blue rounded-[8px] text-white">
											<SypacIcon
												iconName="Bell"
												size="custom"
												width="32px"
												height="32px"
											/>
										</div>

										<div className="flex gap-[19px] flex-1 items-start justify-between">
											<div className="flex flex-col gap-2">
												<SypacText variant="body-regular-medium">
													{displayedStepKey && (
														<p className="text-gray-80 text-[20px] leading-[26px] whitespace-nowrap">
															<T keyName={`orderStepper.${displayedStepKey}`}>
																{displayedStepKey}
															</T>
														</p>
													)}
												</SypacText>

												{displayedSubKey ? (
													<div className="flex gap-3 items-start">
														<SypacText variant="body-regular-medium">
															<p className="text-gray-40 text-[14px] leading-[20px]">
																<T keyName={`orderStepper.${displayedSubKey}`}>
																	{displayedSubKey}
																</T>
															</p>
														</SypacText>
														<>
															<div className="w-[2px] h-[2px] bg-gray-40 rounded-full mt-2" />
															<SypacText variant="body-regular-medium">
																<p className="text-gray-40 text-[14px] leading-[20px] whitespace-nowrap">
																	{formatTimeSince(order?.updatedAt)}
																</p>
															</SypacText>
														</>
													</div>
												) : null}
											</div>

											<div className="flex gap-6 items-start">
												<div
													data-tooltip-id="contract-order-hint"
													data-tooltip-content={t(
														'contracts.orderedQuantity',
														'Ordered quantity',
													)}
													className="flex gap-3 items-center whitespace-nowrap"
												>
													<Tons />
													<SypacText variant="body-regular-medium">
														<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
															{qtyTons} <T keyName="ordersTable.tons">tons</T>
														</p>
													</SypacText>
												</div>

												<div
													data-tooltip-id="contract-order-hint"
													data-tooltip-content={t(
														'contracts.orderPriceWithVat',
														'Order price with VAT',
													)}
													className="flex gap-3 items-center whitespace-nowrap"
												>
													<PriceTag />
													<SypacText variant="body-regular-medium">
														<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
															<NumericFormat
																type="text"
																displayType="text"
																thousandSeparator=" "
																decimalSeparator="."
																decimalScale={2}
																value={totalWithVat}
																suffix={
																	order?.product?.currency === 'zł'
																		? ' PLN'
																		: ' MDL'
																}
															/>
														</p>
													</SypacText>
												</div>
											</div>
										</div>
									</div>

									<div className="border-0 border-solid border-t-[1px] border-gray-10" />

									<div className="flex gap-3 px-[10px]">
										<div className="flex flex-col items-center">
											<OrangeCircle />
											<DashedDivider
												direction="vertical"
												height={2}
												length={52}
												dashLength={4}
												gapLength={4}
												color="#5D5D5D"
											/>
											<GreenSquare />
										</div>

										<div className="flex flex-col gap-4">
											<div className="flex flex-col gap-3">
												<div className="h-[16px] flex items-center gap-2">
													<SypacText variant="body-regular-medium">
														<p className="text-[14px] leading-[16px] text-gray-40">
															<T keyName="contractsTable.pickupDate">
																Pickup date:
															</T>
														</p>
													</SypacText>

													<SypacText variant="body-regular-medium">
														<p className="text-[14px] leading-[16px] text-gray-40">
															{formatDate(order.deliveryFrom)}
														</p>
													</SypacText>
												</div>

												<SypacText variant="body-regular-medium">
													<p className="text-[16px] leading-[24px] text-gray-80">
														{pickupAddr}
													</p>
												</SypacText>
											</div>

											<div className="flex flex-col gap-3">
												<div className="h-[16px] flex items-center gap-2">
													<SypacText variant="body-regular-medium">
														<p className="text-[14px] leading-[16px] text-gray-40">
															<T keyName="contractsTable.delivery">Delivery:</T>
														</p>
													</SypacText>

													<SypacText variant="body-regular-medium">
														<p className="text-[14px] leading-[16px] text-gray-40">
															{formatDate(order.deliveryTo)}
														</p>
													</SypacText>
												</div>

												<SypacText variant="body-regular-medium">
													<p
														className="text-[16px] leading-[24px] text-gray-80"
														dangerouslySetInnerHTML={{ __html: deliveryHtml }}
													/>
												</SypacText>
											</div>
										</div>
									</div>

									{trucksCount > 1 && (
										<>
											<div className="border-0 border-solid border-t-[1px] border-gray-10" />

											<div className="flex flex-col gap-[10px] px-[10px]">
												<LayeredProgressBar
													type={{
														key: 'fleetStatus',
														label: 'Order execution',
													}}
													totalTons={totalTrucksForFleet}
													segments={[
														{
															value: deliveredTrucks,
															colorClass: 'bg-mountain-meadow',
															tooltip: `${t(
																'layeredProgressBar.delivered',
																'Delivered:',
															)} ${deliveredTrucks} ${t(
																'layeredProgressBar.trucks',
																'trucks',
															)}`,
															enableBrightnessHover: true,
														},
														{
															value: inProgressTrucks,
															colorClass: 'bg-texas-rose',
															tooltip: `${t(
																'ordersTab.inProgress',
																'In progress:',
															)} ${inProgressTrucks} ${t(
																'layeredProgressBar.trucks',
																'trucks',
															)}`,
															enableBrightnessHover: true,
														},
														{
															value: toBeDoneTrucks,
															colorClass: 'bg-[#E8E8E8]',
															tooltip: `${t(
																'ordersTab.toBeDone',
																'To be done:',
															)} ${toBeDoneTrucks} ${t(
																'layeredProgressBar.trucks',
																'trucks',
															)}`,
														},
													]}
												/>
											</div>
										</>
									)}
								</div>
							);
						})
					) : (
						<div className="py-6 text-gray-40 text-center">
							<SypacText variant="body-regular-medium">
								<p>
									{(contract?.orders?.length ?? 0) > 0 ? (
										<T keyName="orders.noOrdersMatchFilters">
											No orders match your filters.
										</T>
									) : (
										<T keyName="contracts.noOrders">
											No orders found for this contract.
										</T>
									)}
								</p>
							</SypacText>
						</div>
					)}
				</div>
			</div>
			<Tooltip
				place="top"
				id="contract-order-hint"
				style={{
					backgroundColor: '#E8E8E8',
					color: '#000000',
				}}
			/>
		</>
	);
};

export default OrdersTab;
